import { useLayoutEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { MoolahProvider } from './contexts/MoolahContext';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { ErrorBoundary } from './components/common/ErrorBoundary';
import { BottomNavigation, shouldShowBottomNav } from './components/BottomNavigation';
import { TopBanner } from './components/TopBanner';
import { useAppViewportHeight } from './hooks/useAppViewportHeight';
import { captureMetaClickIds } from './utils/metaClickIds';

// Pages
import { LoginPage } from './pages/LoginPage';
import { RegisterPage } from './pages/RegisterPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { ChangeMobilePage } from './pages/ChangeMobilePage';
import { SetAppPasswordPage } from './pages/SetAppPasswordPage';
import { VerifyLoginOtpPage } from './pages/VerifyLoginOtpPage';
import { DashboardPage } from './pages/DashboardPage';
import { SendMoneyPage } from './pages/SendMoneyPage';
import { TransactPage } from './pages/TransactPage';
import { VouchersPage } from './pages/VouchersPage';
import { QRPaymentPage } from './pages/QRPaymentPage';
import BuyUsdcPage from './pages/BuyUsdcPage';
import { ProfilePage } from './pages/ProfilePage';
import { TransactionHistoryPage } from './pages/TransactionHistoryPage';
import { WalletSettingsPage } from './pages/WalletSettingsPage';
import { NotificationSettingsPage } from './pages/NotificationSettingsPage';
import { RequestMoneyPage } from './pages/RequestMoneyPage';
import { ServicesPage } from './pages/ServicesPage';
import { SupportPage } from './pages/SupportPage';
import FeedbackPage from './pages/FeedbackPage';
import { ReferralPage } from './pages/ReferralPage';
import { LoyaltyPromotionsPage } from './pages/LoyaltyPromotionsPage';

// Overlay Components
import { AirtimeDataOverlay } from './components/overlays/AirtimeDataOverlay';
// AirtimeDataOverlayModern - Available but using old overlay for stability in Codespaces
// import { AirtimeDataOverlayModern } from './components/overlays/airtime-data/AirtimeDataOverlayModern';
import { ElectricityOverlay } from './components/overlays/ElectricityOverlay';
import { BillPaymentOverlay } from './components/overlays/BillPaymentOverlay';
import { FlashEeziCashOverlay } from './components/overlays/flash-eezicash/FlashEeziCashOverlay';
import { MMCashRetailOverlay } from './components/overlays/mmcash-retail/MMCashRetailOverlay';
import { WithdrawCashOverlay } from './components/overlays/withdraw-cash/WithdrawCashOverlay';
import { TopupEasyPayOverlay } from './components/overlays/topup-easypay/TopupEasyPayOverlay';
import { DigitalVouchersOverlay } from './components/overlays/digital-vouchers/DigitalVouchersOverlay';
import { TapToAddMoneyOverlay } from './components/overlays/TapToAddMoneyOverlay';
import { AddMoneyEftOverlay } from './components/overlays/AddMoneyEftOverlay';
import { TopupVoucherOverlay } from './components/overlays/topup-voucher/TopupVoucherOverlay';
import { ClubsOverlay } from './components/overlays/clubs/ClubsOverlay';
import { GlobalServicesOverlay } from './components/overlays/global-services/GlobalServicesOverlay';

// KYC Pages
import { KYCDocumentsPage } from './pages/KYCDocumentsPage';
import { KYCStatusPage } from './pages/KYCStatusPage';

// Layout Components
// import { MobileLayout } from './layouts/MobileLayout';

function AppContent() {
  const location = useLocation();
  const mainContentRef = useRef<HTMLDivElement | null>(null);
  useAppViewportHeight();

  const showBottomNavigation = shouldShowBottomNav(location.pathname);
  
  // Pages that should show the top banner (main app pages)
  const pagesWithTopBanner = ['/dashboard', '/send-money', '/buy-usdc', '/transact', '/qr-payment', '/vouchers', '/profile', '/transactions', '/wallet-settings', '/notification-settings', '/request-money', '/services', '/support', '/feedback', '/electricity', '/bill-payments', '/referrals', '/airtime-data-overlay', '/electricity-overlay', '/bill-payment-overlay', '/flash-eezicash-overlay', '/mmcash-retail-overlay', '/withdraw-cash-overlay', '/atm-cashsend-overlay', '/topup-easypay', '/vouchers-overlay', '/gift-cards-overlay', '/tap-to-add-money', '/add-money-eft', '/topup-voucher', '/clubs-overlay', '/kyc', '/kyc/documents', '/kyc/status'];
  const showTopBanner = pagesWithTopBanner.includes(location.pathname);

  useLayoutEffect(() => {
    captureMetaClickIds(window.location.search || location.search);
  }, [location.search]);

  useLayoutEffect(() => {
    const scrollContainer = mainContentRef.current;
    if (scrollContainer) {
      scrollContainer.scrollTop = 0;
      scrollContainer.scrollLeft = 0;
    }
  }, [location.pathname]);

  return (
    <div className="wallet-app-shell">
      {/* Single Mobile Container - 375px max width, centered */}
      <div className="wallet-app-column">
        {/* Top Banner - only show on main app pages */}
        {showTopBanner && <TopBanner />}
        
        {/* Main Content Area - Flex 1 to take remaining space */}
        <div 
          ref={mainContentRef}
          style={{
            flex: 1,
            overflow: 'auto',
            backgroundColor: '#ffffff'
          }}
        >
          <Routes>
            {/* Public Routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/register/:slug" element={<RegisterPage />} />
            <Route path="/signup" element={<RegisterPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/change-mobile" element={<ChangeMobilePage />} />
            <Route path="/set-app-password" element={<SetAppPasswordPage />} />
            <Route path="/verify-login-otp" element={<VerifyLoginOtpPage />} />
            
            {/* KYC Routes */}
            <Route path="/kyc" element={<ProtectedRoute><KYCDocumentsPage /></ProtectedRoute>} />
            <Route path="/kyc/documents" element={<ProtectedRoute><KYCDocumentsPage /></ProtectedRoute>} />
            <Route path="/kyc/status" element={<ProtectedRoute><KYCStatusPage /></ProtectedRoute>} />
            
            {/* FIXED: Direct protected routes - bypassing nested routing for now */}
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
            <Route path="/send-money" element={<ProtectedRoute><SendMoneyPage /></ProtectedRoute>} />
            <Route path="/buy-usdc" element={<ProtectedRoute><BuyUsdcPage /></ProtectedRoute>} />
            <Route path="/transact" element={<ProtectedRoute><TransactPage /></ProtectedRoute>} />
            <Route path="/qr-payment" element={<ProtectedRoute><QRPaymentPage /></ProtectedRoute>} />
            <Route path="/vouchers" element={<ProtectedRoute><VouchersPage /></ProtectedRoute>} />
            <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
            <Route path="/wallet-settings" element={<ProtectedRoute><WalletSettingsPage /></ProtectedRoute>} />
            <Route path="/notification-settings" element={<ProtectedRoute><NotificationSettingsPage /></ProtectedRoute>} />
            <Route path="/transactions" element={<ProtectedRoute><TransactionHistoryPage /></ProtectedRoute>} />
            <Route path="/request-money" element={<ProtectedRoute><RequestMoneyPage /></ProtectedRoute>} />
            <Route path="/services" element={<ProtectedRoute><ServicesPage /></ProtectedRoute>} />
            <Route path="/support" element={<ProtectedRoute><SupportPage /></ProtectedRoute>} />
            <Route path="/feedback" element={<ProtectedRoute><FeedbackPage /></ProtectedRoute>} />
            <Route path="/referrals" element={<ProtectedRoute><ReferralPage /></ProtectedRoute>} />
            <Route path="/loyalty-promotions" element={<ProtectedRoute><LoyaltyPromotionsPage /></ProtectedRoute>} />
            <Route path="/electricity" element={<Navigate to="/electricity-overlay" replace />} />
            <Route path="/bill-payments" element={<Navigate to="/bill-payment-overlay" replace />} />
            
            {/* Overlay Routes */}
            <Route path="/airtime-data-overlay" element={<ProtectedRoute><AirtimeDataOverlay /></ProtectedRoute>} />
            <Route path="/electricity-overlay" element={<ProtectedRoute><ElectricityOverlay /></ProtectedRoute>} />
            <Route path="/bill-payment-overlay" element={<ProtectedRoute><BillPaymentOverlay /></ProtectedRoute>} />
            <Route path="/flash-eezicash-overlay" element={<ProtectedRoute><FlashEeziCashOverlay /></ProtectedRoute>} />
            <Route path="/mmcash-retail-overlay" element={<ProtectedRoute><MMCashRetailOverlay /></ProtectedRoute>} />
            <Route path="/withdraw-cash-overlay" element={<ProtectedRoute><WithdrawCashOverlay /></ProtectedRoute>} />
            <Route path="/atm-cashsend-overlay" element={<ProtectedRoute><WithdrawCashOverlay /></ProtectedRoute>} />
            <Route path="/topup-easypay" element={<ProtectedRoute><TopupEasyPayOverlay /></ProtectedRoute>} />
            <Route path="/vouchers-overlay" element={<ProtectedRoute><DigitalVouchersOverlay /></ProtectedRoute>} />
            <Route path="/gift-cards-overlay" element={<ProtectedRoute><DigitalVouchersOverlay mode="gift-cards" /></ProtectedRoute>} />
            <Route path="/tap-to-add-money" element={<ProtectedRoute><TapToAddMoneyOverlay /></ProtectedRoute>} />
            <Route path="/add-money-eft" element={<ProtectedRoute><AddMoneyEftOverlay /></ProtectedRoute>} />
            <Route path="/topup-voucher" element={<ProtectedRoute><TopupVoucherOverlay /></ProtectedRoute>} />
            <Route path="/clubs-overlay" element={<ProtectedRoute><ClubsOverlay /></ProtectedRoute>} />
            <Route path="/global-services-overlay" element={<ProtectedRoute><GlobalServicesOverlay /></ProtectedRoute>} />
            {/* Catch-all redirect */}
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </div>

        {showBottomNavigation && (
          <div
            style={{
              flexShrink: 0,
              position: 'relative',
              zIndex: 50,
              width: '100%',
            }}
          >
            <BottomNavigation />
          </div>
        )}
        

      </div>
    </div>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <MoolahProvider>
          <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <AppContent />
          </Router>
        </MoolahProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}